/* Viulet V32 — continuous photographic frame joins and gap-free cast shadows.
   Shared by every Mirror: an artwork may be wider or taller than it is square. Its shape is two factors of
   the nominal size ({w, h}; 1 and 1 for a square), carried by assets.artwork.shape. */
(function (root) {
  'use strict';

  const SIZES = [20];
  const PRESENTATIONS = ['Untethered', 'Lined', 'Crowned'];
  // Lined and Crowned share one frame, 0.75 in, as on the Letters page (the moving shell is built for it).
  // Crowned adds a 1.75 in mat inside it, as on Letters: a 20 x 20 in print measures 25 x 25 in overall.
  const FRAME_IN = 0.75, CROWNED_MAT_IN = 1.75;
  const DEFAULT_SELECTION = { size: 20, presentation: 'Untethered', finish: 'Seraph White', mat: 'Cream' };
  const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
  const mix = (a, b, t) => a + (b - a) * t;
  const number = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const imageSource = (asset) => typeof asset === 'string' ? asset : asset && (asset.src || asset.path);

  function copySurface(context, surface) {
    // All intermediate surfaces share the destination's backing grid. Copy pixels
    // directly: scaling a rounded backing width back to fractional CSS dimensions
    // would resample their edges a second time and move them relative to the print.
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.drawImage(surface, 0, 0);
    context.restore();
  }

  function clearSurface(context, surface) {
    // Clear complete backing pixels, including the rounded final row/column.
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, surface.width, surface.height);
    context.restore();
  }

  function dimensions(size, presentation, matInches) {
    const frame = presentation === 'Untethered' ? 0 : FRAME_IN;
    const mat = frame ? clamp(number(matInches, presentation === 'Crowned' ? CROWNED_MAT_IN : 0), 0, CROWNED_MAT_IN) : 0;
    return { print: size, frame, mat, opening: size + 2 * mat, outer: size + 2 * (frame + mat) };
  }

  function styleKey(selection) {
    // Hidden controls cannot start a visual transition.
    if (selection.presentation === 'Untethered') return 'Untethered';
    return [selection.presentation, selection.finish, selection.presentation === 'Crowned' ? selection.mat : ''].join('|');
  }

  function surfaceKey(selection) {
    // Lined and Crowned are the same physical frame. Only its materials can crossfade.
    return selection.presentation === 'Untethered' ? 'Untethered' : ['Framed', selection.finish, selection.mat].join('|');
  }

  function spring(value, velocity, target, dt, frequency) {
    // Analytic critically damped spring. Interrupted changes retain position AND velocity.
    const delta = value - target;
    const decay = Math.exp(-frequency * dt);
    const coefficient = velocity + frequency * delta;
    const next = target + (delta + coefficient * dt) * decay;
    const nextVelocity = (velocity - frequency * coefficient * dt) * decay;
    return Math.abs(next - target) < 0.00001 && Math.abs(nextVelocity) < 0.0001
      ? { value: target, velocity: 0 } : { value: next, velocity: nextVelocity };
  }

  function interpolatedLayout(size, layouts, fallback) {
    const low = size <= 30 ? 20 : 30;
    const high = low + 10;
    const progress = clamp((size - low) / 10, 0, 1);
    const a = Object.assign({}, fallback(low), layouts && layouts[low]);
    const b = Object.assign({}, fallback(high), layouts && layouts[high]);
    const result = {};
    Object.keys(a).forEach(key => { result[key] = mix(number(a[key], 0), number(b[key], a[key]), progress); });
    return result;
  }

  function layoutFor(size, view, background) {
    const config = background || {};
    const photographic = !!imageSource(config);
    const fallback = nominal => ({
      fieldWidthIn: view === 'scale' ? number(config.scaleFieldWidthIn, 50) : photographic ? 48 + (nominal - 20) * 0.8 : (nominal + 5) / 0.78,
      artCenterX: number(config.artCenterX, 0.5),
      artCenterY: number(config.artCenterY, 0.43),
      sceneFit: config.sceneFit === 'contain' ? 1 : 0,
      scale: number(config.scale, 1),
      anchorX: number(config.anchorX, 0.5),
      anchorY: number(config.anchorY, 0.5)
    });
    const result = interpolatedLayout(size, view === 'focus' ? config.focusLayouts : config.scaleLayouts, fallback);
    // A calibrated field remains fixed across print sizes and presentations.
    if (view === 'scale') result.fieldWidthIn = number(config.scaleFieldWidthIn, 50);
    else result.fieldWidthIn = number(config.focusFieldWidthIn, result.fieldWidthIn);
    result.fieldWidthIn = Math.max(20, result.fieldWidthIn);
    result.scale = Math.max(0.1, result.scale);
    return result;
  }

  function shapeOf(artwork) {
    const s = artwork && artwork.shape || {}, m = artwork && artwork.matScale || {};
    const factor = value => Number(value) > 0 ? clamp(Number(value), 0.25, 4) : 1;
    const scale = value => Number(value) >= 0 && value !== null && value !== '' ? clamp(Number(value), 0, 2) : 1;
    return { w: factor(s.w), h: factor(s.h), mx: scale(m.mx), my: scale(m.my) };
  }

  function geometry(width, height, size, presentation, layout, matInches, shape) {
    const physical = dimensions(size, presentation, matInches);
    const sx = shape ? shape.w : 1, sy = shape ? shape.h : 1;
    const fit = clamp(number(layout.sceneFit, 0), 0, 1);
    const sceneSide = Math.min(width, height);
    const referenceWidth = mix(Math.min(width, height * 1.02), sceneSide, fit);
    const ppi = referenceWidth / layout.fieldWidthIn;
    const centerX = mix(width * layout.artCenterX, (width - sceneSide) / 2 + sceneSide * layout.artCenterX, fit);
    const centerY = mix(height * layout.artCenterY, (height - sceneSide) / 2 + sceneSide * layout.artCenterY, fit);
    // The frame adds the same inches on every side. The mat may not: a work that is wider or taller than it is square
    // can have its own Crowned measurements across its width and its height (shape.mx and shape.my scale the mat).
    const mx = shape && shape.mx >= 0 ? shape.mx : 1, my = shape && shape.my >= 0 ? shape.my : 1;
    const rectangle = (frameIn, matIn) => { const w = (size * sx + 2 * (frameIn + matIn * mx)) * ppi, h = (size * sy + 2 * (frameIn + matIn * my)) * ppi; return { x: centerX - w / 2, y: centerY - h / 2, width: w, height: h }; };
    return { physical, ppi, centerX, centerY, art: rectangle(0, 0), opening: rectangle(0, physical.mat), outer: rectangle(physical.frame, physical.mat), facePx: physical.frame * ppi, matPx: physical.mat * Math.min(mx, my) * ppi };
  }

  function shellGeometry(width, height, size, layout, railExtent, shape) {
    // A negative extent tucks the same full-width photographic rails behind the print.
    // Nominal product dimensions stay in dimensions(); only the displayed shell moves.
    const extent = clamp(railExtent, -FRAME_IN, CROWNED_MAT_IN);
    const bounds = geometry(width, height, size, 'Lined', layout, Math.max(0, extent), shape);
    if (extent >= 0) return bounds;
    bounds.physical.opening = size + 2 * extent;
    bounds.physical.outer = size + 2 * (0.75 + extent);
    const sx = shape ? shape.w : 1, sy = shape ? shape.h : 1;
    const rectangle = inches => { const extra = inches - size, w = (size * sx + extra) * bounds.ppi, h = (size * sy + extra) * bounds.ppi; return { x: bounds.centerX - w / 2, y: bounds.centerY - h / 2, width: w, height: h }; };
    bounds.opening = rectangle(bounds.physical.opening);
    bounds.outer = rectangle(bounds.physical.outer);
    return bounds;
  }

  function nineSlice(width, height, border, destination, face, bleed = 0) {
    const xs = [0, border, width - border, width];
    const ys = [0, border, height - border, height];
    const dx = [destination.x, destination.x + face, destination.x + destination.width - face, destination.x + destination.width];
    const dy = [destination.y, destination.y + face, destination.y + destination.height - face, destination.y + destination.height];
    const slices = [];
    for (let row = 0; row < 3; row++) {
      for (let column = 0; column < 3; column++) {
        if (row === 1 && column === 1) continue;
        const sourceWidth = xs[column + 1] - xs[column];
        const sourceHeight = ys[row + 1] - ys[row];
        const targetWidth = dx[column + 1] - dx[column];
        const targetHeight = dy[row + 1] - dy[row];
        // Adjacent photographic tiles share a full backing pixel at their joins.
        // Independent antialiased edges otherwise leave a translucent hairline
        // through the corner even though the source atlas is fully opaque there.
        // Extend the source by the same scale so the original texture stays put.
        const left = column > 0 && row !== 1 ? bleed : 0;
        const right = column < 2 && row !== 1 ? bleed : 0;
        const top = row > 0 && column !== 1 ? bleed : 0;
        const bottom = row < 2 && column !== 1 ? bleed : 0;
        const scaleX = sourceWidth / targetWidth;
        const scaleY = sourceHeight / targetHeight;
        slices.push([xs[column] - left * scaleX, ys[row] - top * scaleY,
          sourceWidth + (left + right) * scaleX, sourceHeight + (top + bottom) * scaleY,
          dx[column] - left, dy[row] - top, targetWidth + left + right, targetHeight + top + bottom]);
      }
    }
    return slices;
  }

  function ringPath(context, outer, inner) {
    context.beginPath();
    context.rect(outer.x, outer.y, outer.width, outer.height);
    context.rect(inner.x, inner.y, inner.width, inner.height);
  }

  function lightingFor(background) {
    const config = background || {};
    const photo = !!imageSource(config);
    const supplied = config.lighting || {};
    const defaults = {
      strength: number(config.shadowStrength, 1), softness: number(config.shadowSoftness, 1),
      castBlur: photo ? 0.58 : 1.08, castX: photo ? 0.20 : 0.14, castY: photo ? 0.38 : 0.36,
      castOpacity: photo ? 0.13 : 0.12,
      contactBlur: photo ? 0.10 : 0.17, contactX: photo ? 0.02 : 0.025, contactY: photo ? 0.045 : 0.075,
      contactOpacity: photo ? 0.12 : 0.10,
      frameDepth: 0.5, recessStrength: 1, printLift: 0.18, printShadowStrength: 1,
      unframedStrength: 0.9, unframedLift: 0.85,
      environmentAmount: photo ? 0.10 : 0, environmentBlur: 7, productExposure: 1
    };
    const result = {};
    Object.keys(defaults).forEach(key => {
      const value = number(supplied[key], defaults[key]);
      // A low practical lamp can cast a shadow upwards. Directions are signed;
      // opacity, blur and depth must remain nonnegative.
      result[key] = key === 'productExposure' ? clamp(value, 0.02, 1) : /^(cast|contact)[XY]$/.test(key) ? value : Math.max(0, value);
    });
    return result;
  }

  function loadImage(asset) {
    if (asset && asset.image) return Promise.resolve(asset.image);
    const src = imageSource(asset);
    if (!src) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not load gallery asset: ' + src));
      img.src = src;
    });
  }

  // A fixed reflected window in the room's inch coordinates. Changing the print
  // aperture reveals another crop; it never stretches the window to fit a frame.
  const REFLECTION_FIELD = { x: -24, y: -28, width: 24, height: 52, pixelsWide: 256, pixelsHigh: 512 };

  function surfaceReflectionFor(background) {
    const supplied = background && background.surfaceReflection;
    if (!imageSource(background) || !supplied || supplied.type !== 'soft-window') return null;
    return {
      opacity: clamp(number(supplied.opacity, 0.04), 0, 0.12),
      highlightOpacity: clamp(number(supplied.highlightOpacity, 0.058), 0, 0.12),
      offsetXIn: number(supplied.offsetXIn, 0), offsetYIn: number(supplied.offsetYIn, 0)
    };
  }

  function reflectionAt(x, y, profile) {
    const smooth = (low, high, value) => {
      const t = clamp((value - low) / (high - low), 0, 1);
      return t * t * (3 - 2 * t);
    };
    const band = (value, low, high, softness) => smooth(low - softness, low + softness, value) * (1 - smooth(high - softness, high + softness, value));
    // A slightly receding side window, with empty mullions between panes. Broad
    // irregular daylight is intentionally quiet: no drawn grid or diagonal glare.
    const u = (x + 0.022 * (y + 12)) / (1 - 0.004 * (y + 12));
    const v = y - 0.11 * (u + 16);
    const panes = band(u, -21.6, -16.2, 0.34) * 0.87
      + band(u, -15.35, -10.05, 0.34)
      + band(u, -9.2, -8.15, 0.27) * 0.63;
    const windowHeight = band(v, -26, 19, 1.25);
    const crossbar = 1 - band(v, -3.65, -2.75, 0.27);
    const daylight = (0.77 + 0.10 * Math.sin(u * 0.62 + v * 0.19) + 0.08 * Math.cos(v * 0.43 - u * 0.21))
      * (1 - 0.30 * smooth(1, 19, v));
    const softFoliage = 1 - 0.22 * Math.exp(-((u + 14) ** 2 / 13 + (v - 8) ** 2 / 23))
      - 0.16 * Math.exp(-((u + 19) ** 2 / 7 + (v - 1) ** 2 / 18));
    const highlight = Math.exp(-((u + 13.9) ** 2 / 0.65 + (v + 16) ** 2 / 12));
    return clamp(panes * windowHeight * crossbar * (profile.opacity * daylight * softFoliage
      + Math.max(0, profile.highlightOpacity - profile.opacity) * highlight), 0, 0.12);
  }

  function reflectionPlacement(bounds, profile) {
    return [bounds.centerX + (REFLECTION_FIELD.x + profile.offsetXIn) * bounds.ppi,
      bounds.centerY + (REFLECTION_FIELD.y + profile.offsetYIn) * bounds.ppi,
      REFLECTION_FIELD.width * bounds.ppi, REFLECTION_FIELD.height * bounds.ppi];
  }

  class ViuletGalleryRenderer {
    constructor(canvas, assets) {
      if (!canvas || !canvas.getContext) throw new TypeError('A canvas element is required.');
      this.canvas = canvas;
      this.context = canvas.getContext('2d', { alpha: false, colorSpace: 'srgb' });
      if (!this.context) throw new Error('This browser does not support Canvas 2D.');
      this.assets = assets || {};
      this.shape = shapeOf(this.assets.artwork);
      this.selection = Object.assign({}, DEFAULT_SELECTION);
      this.view = 'focus';
      this.background = this.assets.backgrounds && this.assets.backgrounds.gallery ? 'gallery' : Object.keys(this.assets.backgrounds || {})[0] || 'gallery';
      this.images = { frames: {}, mats: {}, backgrounds: {}, artwork: null };
      this.layers = new Map([[surfaceKey(this.selection), { selection: Object.assign({}, this.selection), weight: 1, start: 1 }]]);
      this.backgroundLayers = new Map([[this.background, { weight: 1, start: 1 }]]);
      this.styleTransition = null;
      this.backgroundTransition = null;
      this.motion = {};
      this.currentSize = 20;
      this.sizeVelocity = 0;
      this.matMotion = { value: 0, target: 0, velocity: 0 };
      this.frameReveal = false;
      this.loaded = false;
      this.destroyed = false;
      this.frameRequest = null;
      this.lastTime = null;
      this.artworkRequest = 0;
      this.reflectionFields = new Map();
      this.maxDPR = 2;
      this.width = 0;
      this.height = 0;
      this.reducedMotion = !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.shadowCanvas = canvas.ownerDocument.createElement('canvas');
      this.shadowContext = this.shadowCanvas.getContext('2d');
      this.shellCanvas = canvas.ownerDocument.createElement('canvas');
      this.shellContext = this.shellCanvas.getContext('2d');
      this.productCanvas = canvas.ownerDocument.createElement('canvas');
      this.productContext = this.productCanvas.getContext('2d');
      this._tick = this._tick.bind(this);
      this._onResize = this.resize.bind(this);
      this._refreshTargets(true);
      if (typeof root.ResizeObserver === 'function') {
        this.resizeObserver = new root.ResizeObserver(this._onResize);
        this.resizeObserver.observe(canvas);
      }
      if (root.addEventListener) root.addEventListener('resize', this._onResize, { passive: true });
      this.resize();
    }

    async load() {
      if (this.loadPromise) return this.loadPromise;
      this.loadPromise = (async () => {
        const jobs = [];
        const artworkRequest = this.artworkRequest;
        const artwork = this.assets.artwork && (this.assets.artwork.src || this.assets.artwork.path || this.assets.artwork.image ? this.assets.artwork : this.assets.artwork.display);
        jobs.push(loadImage(artwork).then(image => { if (!this.destroyed && artworkRequest === this.artworkRequest) this.images.artwork = image; }));
        ['frames', 'mats', 'backgrounds'].forEach(group => {
          Object.entries(this.assets[group] || {}).forEach(([key, asset]) => {
            jobs.push(loadImage(asset).then(image => { if (!this.destroyed) this.images[group][key] = image; }));
          });
        });
        await Promise.all(jobs);
        if (!this.destroyed) {
          this.loaded = true;
          this.resize();
          this._schedule();
        }
        return this;
      })();
      return this.loadPromise;
    }

    setSelection(selection, options) {
      if (this.destroyed) return this;
      const next = Object.assign({}, this.selection, selection || {});
      next.size = number(next.size, this.selection.size);
      if (!SIZES.includes(next.size)) throw new RangeError('Print size must be 20, 30, or 40 inches.');
      if (!PRESENTATIONS.includes(next.presentation)) throw new RangeError('Presentation must be Untethered, Lined, or Crowned.');
      const immediate = !!(options && options.immediate) || this.reducedMotion;
      const previousKey = surfaceKey(this.selection);
      const nextKey = surfaceKey(next);
      const matTarget = next.presentation === 'Crowned' ? CROWNED_MAT_IN : 0;
      const visibleFrame = Array.from(this.layers.values()).some(layer => layer.weight > 0 && layer.selection.presentation !== 'Untethered');
      const reveal = this.frameReveal || (this.selection.presentation === 'Untethered' && next.presentation === 'Crowned') ||
        (next.presentation === 'Untethered' && (this.selection.presentation === 'Crowned' || this.matMotion.value > 0));
      if (immediate) {
        this.matMotion = { value: matTarget, target: matTarget, velocity: 0 };
        this.frameReveal = false;
        this.layers = new Map([[nextKey, { selection: Object.assign({}, next), weight: 1, start: 1 }]]);
        this.styleTransition = null;
      } else if (reveal) {
        this.frameReveal = true;
        if (!visibleFrame && next.presentation !== 'Untethered') {
          this.matMotion = { value: -0.75, target: matTarget, velocity: 0 };
          this.layers = new Map([[nextKey, { selection: Object.assign({}, next), weight: 1, start: 1 }]]);
          this.styleTransition = null;
        } else {
          // Presentation reveals never fade to an Untethered layer. Keep any material
          // blend on the one shared shell while it moves behind the opaque artwork.
          this.layers.forEach((layer, key) => { if (layer.selection.presentation === 'Untethered') this.layers.delete(key); });
          const total = Array.from(this.layers.values()).reduce((sum, layer) => sum + layer.weight, 0);
          if (total > 0 && total !== 1) this.layers.forEach(layer => { layer.weight /= total; layer.start = layer.weight; });
          if (this.styleTransition && this.styleTransition.target === 'Untethered') this.styleTransition = null;
          if (next.presentation !== 'Untethered') {
            const activeKey = this.styleTransition ? this.styleTransition.target : this.layers.keys().next().value;
            if (activeKey !== nextKey) {
              if (!this.layers.has(nextKey)) this.layers.set(nextKey, { selection: Object.assign({}, next), weight: 0, start: 0 });
              this.layers.forEach(layer => { layer.start = layer.weight; });
              this.styleTransition = { elapsed: 0, duration: 0.68, target: nextKey };
            }
          }
        }
        this.matMotion.target = next.presentation === 'Untethered' ? -0.75 : matTarget;
      } else if (!visibleFrame) {
        // Preserve the established Untethered/Lined fade at the print edge.
        this.matMotion = { value: 0, target: matTarget, velocity: 0 };
      } else if (next.presentation !== 'Untethered') {
        // Retarget the same rails from their displayed position and velocity.
        this.matMotion.target = matTarget;
      }
      this.selection = next;
      if (!immediate && !reveal && previousKey !== nextKey) {
        if (!this.layers.has(nextKey)) this.layers.set(nextKey, { selection: Object.assign({}, next), weight: 0, start: 0 });
        this.layers.forEach(layer => { layer.start = layer.weight; });
        this.styleTransition = { elapsed: 0, duration: 0.68, target: nextKey };
      }
      this._retireOccludedFrame();
      if (immediate) { this.currentSize = next.size; this.sizeVelocity = 0; }
      this._refreshTargets(immediate);
      this._schedule();
      return this;
    }

    setView(view, options) {
      if (!['focus', 'scale'].includes(view)) throw new RangeError('View must be focus or scale.');
      this.view = view;
      this._refreshTargets(!!(options && options.immediate) || this.reducedMotion);
      this._schedule();
      return this;
    }

    setBackground(id, options) {
      if (!(this.assets.backgrounds || {})[id]) throw new RangeError('Unknown background: ' + id);
      const immediate = !!(options && options.immediate) || this.reducedMotion;
      if (id !== this.background || immediate) {
        const previous = (this.assets.backgrounds || {})[this.background] || {};
        const next = (this.assets.backgrounds || {})[id] || {};
        const sharedSetting = previous.settingId && previous.settingId === next.settingId;
        this.background = id;
        if (immediate) {
          this.backgroundLayers = new Map([[id, { weight: 1, start: 1 }]]);
          this.backgroundTransition = null;
        } else {
          if (!this.backgroundLayers.has(id)) this.backgroundLayers.set(id, { weight: 0, start: 0 });
          this.backgroundLayers.forEach(layer => { layer.start = layer.weight; });
          const duration = sharedSetting ? Math.max(0.01, number(next.lightingTransitionSeconds, number(previous.lightingTransitionSeconds, 0.62))) : 0.62;
          this.backgroundTransition = { elapsed: 0, duration, target: id };
        }
        this._refreshTargets(immediate);
        this._schedule();
      }
      return this;
    }

    async setArtwork(asset) {
      const request = ++this.artworkRequest;
      const image = await loadImage(asset);
      if (!this.destroyed && request === this.artworkRequest) {
        this.assets.artwork = asset;
        this.shape = shapeOf(asset);
        this._refreshTargets(!this._isMoving());
        this.images.artwork = image;
        this._schedule();
      }
      return this;
    }

    resize() {
      if (this.destroyed) return this;
      const bounds = this.canvas.getBoundingClientRect();
      const width = Math.max(1, bounds.width);
      const height = Math.max(1, bounds.height);
      // Bound the four compositing surfaces, without forcing a 2x desktop canvas.
      const dpr = Math.min(clamp(root.devicePixelRatio || 1, 1, this.maxDPR), Math.max(1, Math.sqrt(1200000 / (width * height))));
      const physicalWidth = Math.max(1, Math.round(width * dpr));
      const physicalHeight = Math.max(1, Math.round(height * dpr));
      this.width = width;
      this.height = height;
      this.dpr = dpr;
      // (How far back a work of another shape is seen from depends on the room the picture has. A change that
      // is under way keeps moving toward the new distance; otherwise it is taken at once.)
      if (this.shape.w !== 1 || this.shape.h !== 1) this._refreshTargets(!this._isMoving());
      [this.canvas, this.shadowCanvas, this.shellCanvas, this.productCanvas].forEach(surface => {
        if (surface.width !== physicalWidth || surface.height !== physicalHeight) {
          surface.width = physicalWidth;
          surface.height = physicalHeight;
        }
      });
      // Assigning width/height resets canvas state. Restore the shared CSS-pixel space.
      [this.context, this.shadowContext, this.shellContext, this.productContext].forEach(context => {
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
      });
      this._schedule();
      return this;
    }

    getState() {
      const layout = this._displayedLayout();
      return {
        selection: Object.assign({}, this.selection), view: this.view, background: this.background,
        displayedSize: this.currentSize, displayedMatInches: clamp(this.matMotion.value, 0, CROWNED_MAT_IN),
        displayedRailExtentInches: clamp(this.matMotion.value, -FRAME_IN, CROWNED_MAT_IN), layout,
        geometry: this.selection.presentation !== 'Untethered' || Array.from(this.layers.values()).some(layer => layer.weight > 0 && layer.selection.presentation !== 'Untethered')
          ? shellGeometry(this.width, this.height, this.currentSize, layout, this.matMotion.value, this.shape)
          : geometry(this.width, this.height, this.currentSize, 'Untethered', layout, undefined, this.shape),
        lighting: this._displayedLighting(),
        styleLayers: Array.from(this.layers, ([key, layer]) => ({ key, weight: layer.weight })),
        animating: this._isMoving(), loaded: this.loaded, dpr: this.dpr
      };
    }

    destroy() {
      this.destroyed = true;
      ++this.artworkRequest;
      if (this.frameRequest !== null) root.cancelAnimationFrame(this.frameRequest);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (root.removeEventListener) root.removeEventListener('resize', this._onResize);
      this.frameRequest = null;
      if (this.backgroundCache) { this.backgroundCache.width = this.backgroundCache.height = 1; this.backgroundCache = null; }
      this.layers.clear();
      this.backgroundLayers.clear();
      this.reflectionFields.clear();
      this.images = { frames: {}, mats: {}, backgrounds: {}, artwork: null };
      this.shadowCanvas.width = this.shellCanvas.width = this.productCanvas.width = 1;
      this.shadowCanvas.height = this.shellCanvas.height = this.productCanvas.height = 1;
    }

    _refreshTargets(immediate) {
      const layout = layoutFor(this.selection.size, this.view, (this.assets.backgrounds || {})[this.background]);
      // A composed view gives a work that is wider or taller than it is square the same room around it that a
      // square one has, on whichever side of the picture it comes closest to. (True scale is never changed:
      // the room keeps its inches.)
      if (this.view === 'focus' && (this.shape.w !== 1 || this.shape.h !== 1) && this.width > 0 && this.height > 0) {
        const fit = clamp(number(layout.sceneFit, 0), 0, 1);
        const boxHeight = mix(this.height * 1.02, this.height, fit), reference = Math.min(this.width, boxHeight);
        layout.fieldWidthIn *= Math.max(this.shape.w * reference / this.width, this.shape.h * reference / boxHeight);
      }
      Object.entries(layout).forEach(([key, target]) => {
        if (!this.motion[key] || immediate) this.motion[key] = { value: target, target, velocity: 0 };
        else this.motion[key].target = target;
      });
    }

    _displayedLayout() {
      const result = {};
      Object.entries(this.motion).forEach(([key, motion]) => { result[key] = motion.value; });
      return result;
    }

    _displayedLighting() {
      // Use the visible background weights, including interrupted transitions, so light never jumps.
      const result = {};
      this.backgroundLayers.forEach((layer, key) => {
        const profile = lightingFor((this.assets.backgrounds || {})[key]);
        Object.entries(profile).forEach(([property, value]) => { result[property] = (result[property] || 0) + value * layer.weight; });
      });
      return result;
    }

    _schedule() {
      if (!this.destroyed && this.frameRequest === null) this.frameRequest = root.requestAnimationFrame(this._tick);
    }

    _isMoving() {
      return !!(this.styleTransition || this.backgroundTransition || this.currentSize !== this.selection.size || this.sizeVelocity || this.matMotion.value !== this.matMotion.target || this.matMotion.velocity || Object.values(this.motion).some(value => value.value !== value.target || value.velocity));
    }

    _advanceTransition(property, layers, dt) {
      const transition = this[property];
      if (!transition) return;
      transition.elapsed += dt;
      const progress = clamp(transition.elapsed / transition.duration, 0, 1);
      const eased = progress * progress * (3 - 2 * progress);
      layers.forEach((layer, key) => { layer.weight = mix(layer.start, key === transition.target ? 1 : 0, eased); });
      if (progress === 1) {
        layers.forEach((layer, key) => { if (key !== transition.target) layers.delete(key); });
        this[property] = null;
      }
    }

    _advance(dt) {
      const size = spring(this.currentSize, this.sizeVelocity, this.selection.size, dt, 7);
      this.currentSize = size.value;
      this.sizeVelocity = size.velocity;
      const mat = spring(this.matMotion.value, this.matMotion.velocity, this.matMotion.target, dt, 7);
      this.matMotion.value = mat.value;
      this.matMotion.velocity = mat.velocity;
      Object.values(this.motion).forEach(motion => {
        const value = spring(motion.value, motion.velocity, motion.target, dt, 7);
        motion.value = value.value;
        motion.velocity = value.velocity;
      });
      this._advanceTransition('styleTransition', this.layers, dt);
      this._advanceTransition('backgroundTransition', this.backgroundLayers, dt);
      this._retireOccludedFrame();
      if (this.selection.presentation !== 'Untethered' && this.matMotion.value === this.matMotion.target && !this.matMotion.velocity) this.frameReveal = false;
    }

    _retireOccludedFrame() {
      if (!this.frameReveal || this.selection.presentation !== 'Untethered' || this.matMotion.value > -0.75) return;
      // Removal is safe only when every rail pixel is hidden beneath the print.
      this.layers = new Map([['Untethered', { selection: Object.assign({}, this.selection), weight: 1, start: 1 }]]);
      this.styleTransition = null;
      this.frameReveal = false;
      this.matMotion = { value: 0, target: 0, velocity: 0 };
    }

    _tick(timestamp) {
      this.frameRequest = null;
      if (this.destroyed) return;
      const dt = this.lastTime === null ? 0 : Math.min(0.064, Math.max(0, (timestamp - this.lastTime) / 1000));
      this.lastTime = timestamp;
      this._advance(dt);
      this._draw();
      if (this._isMoving()) this._schedule();
      else this.lastTime = null;
    }

    _draw() {
      const context = this.context;
      const layout = this._displayedLayout();
      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
      context.filter = 'none';
      this._drawBackground(context, layout);
      if (!this.images.artwork) return;
      const artGeometry = geometry(this.width, this.height, this.currentSize, 'Untethered', layout, undefined, this.shape);
      this._drawShadow(context, layout, artGeometry);
      const product = this.productContext;
      clearSurface(product, this.productCanvas);
      product.globalAlpha = 1;
      product.globalCompositeOperation = 'source-over';
      product.filter = 'none';

      // Reuse the cleared shadow surface as an additive, premultiplied shell compositor.
      // This preserves surface opacity when two finishes overlap halfway through a fade.
      const composite = this.shadowContext;
      clearSurface(composite, this.shadowCanvas);
      composite.globalCompositeOperation = 'lighter';
      // Each surface has an independent transparent layer. The print is never in these fades.
      this.layers.forEach(layer => {
        if (layer.weight <= 0.0001 || layer.selection.presentation === 'Untethered') return;
        const shell = this.shellContext;
        clearSurface(shell, this.shellCanvas);
        shell.globalAlpha = 1;
        shell.globalCompositeOperation = 'source-over';
        // All finish layers share a single moving footprint; no old frame is left behind.
        const bounds = shellGeometry(this.width, this.height, this.currentSize, layout, this.matMotion.value, this.shape);
        if (bounds.matPx > 0) this._drawMat(shell, bounds, layer.selection.mat);
        this._drawFrame(shell, bounds, layer.selection.finish);
        this._drawEnvironmentLighting(shell, bounds, layout);
        composite.globalAlpha = layer.weight;
        copySurface(composite, this.shellCanvas);
      });
      composite.globalAlpha = 1;
      composite.globalCompositeOperation = 'source-over';
      copySurface(product, this.shadowCanvas);

      // Exactly one unfiltered artwork draw per frame, at full opacity throughout any change.
      product.globalAlpha = 1;
      product.filter = 'none';
      // The picture fills the print. When its own proportions are not quite the print's (inches that the stored
      // picture does not match exactly), it is cut evenly at two edges; it is never stretched.
      const picture = this.images.artwork, art = artGeometry.art;
      const pw = picture ? picture.naturalWidth || picture.width : 0, ph = picture ? picture.naturalHeight || picture.height : 0;
      const want = art.width / art.height, have = pw > 0 && ph > 0 ? pw / ph : want;
      if (!(Math.abs(have / want - 1) > 0.006)) product.drawImage(picture, art.x, art.y, art.width, art.height);
      else if (have > want) product.drawImage(picture, (pw - ph * want) / 2, 0, ph * want, ph, art.x, art.y, art.width, art.height);
      else product.drawImage(picture, 0, (ph - pw / want) / 2, pw, pw / want, art.x, art.y, art.width, art.height);
      this._drawSurfaceReflection(product, artGeometry);
      this._applyProductExposure(this._displayedLighting().productExposure);
      copySurface(context, this.productCanvas);
    }

    _drawSurfaceReflection(context, bounds) {
      this.backgroundLayers.forEach((layer, key) => {
        // Gallery and detail views remain the original colour reference. Only an
        // explicitly configured, loaded photographic room can opt into this pass.
        if (key === 'gallery' || layer.weight <= 0 || !this.images.backgrounds[key]) return;
        const profile = surfaceReflectionFor((this.assets.backgrounds || {})[key]);
        if (!profile || profile.opacity <= 0) return;
        const cacheKey = [profile.opacity, profile.highlightOpacity].join('|');
        let field = this.reflectionFields.get(cacheKey);
        if (!field) {
          field = this.canvas.ownerDocument.createElement('canvas');
          field.width = REFLECTION_FIELD.pixelsWide; field.height = REFLECTION_FIELD.pixelsHigh;
          const paint = field.getContext('2d');
          // The decoration is optional on constrained or recording-only canvases.
          if (!paint || !paint.createImageData || !paint.putImageData) return;
          const pixels = paint.createImageData(field.width, field.height);
          for (let row = 0; row < field.height; row++) for (let column = 0; column < field.width; column++) {
            const x = REFLECTION_FIELD.x + (column + 0.5) / field.width * REFLECTION_FIELD.width;
            const y = REFLECTION_FIELD.y + (row + 0.5) / field.height * REFLECTION_FIELD.height;
            const index = (row * field.width + column) * 4;
            pixels.data[index] = 251; pixels.data[index + 1] = 252; pixels.data[index + 2] = 248;
            pixels.data[index + 3] = Math.round(reflectionAt(x, y, profile) * 255);
          }
          paint.putImageData(pixels, 0, 0);
          this.reflectionFields.set(cacheKey, field);
        }
        // Restrict to complete print backing pixels. Fractional edge coverage and
        // every adjacent mat/frame pixel stay exactly as the product compositor made them.
        const left = Math.ceil(bounds.art.x * this.dpr) / this.dpr;
        const top = Math.ceil(bounds.art.y * this.dpr) / this.dpr;
        const right = Math.floor((bounds.art.x + bounds.art.width) * this.dpr) / this.dpr;
        const bottom = Math.floor((bounds.art.y + bounds.art.height) * this.dpr) / this.dpr;
        if (right <= left || bottom <= top) return;
        context.save();
        context.beginPath(); context.rect(left, top, right - left, bottom - top); context.clip();
        context.globalCompositeOperation = 'screen';
        context.globalAlpha = clamp(layer.weight, 0, 1);
        context.filter = 'none';
        context.drawImage(field, ...reflectionPlacement(bounds, profile));
        context.restore();
      });
    }

    _applyProductExposure(exposure) {
      if (exposure >= 1) return;
      // Shade the already-composited product, preserving its coverage alpha.
      // A second geometry mask would shade an antialiased boundary twice.
      const product = this.productContext;
      product.save();
      product.globalCompositeOperation = 'source-atop';
      product.globalAlpha = 1 - exposure;
      product.fillStyle = '#000';
      product.fillRect(0, 0, this.width, this.height);
      product.restore();
    }

    _drawBackground(context, layout) {
      // Photographic stills are unchanged during finish selection. Video/canvas
      // backgrounds remain live and bypass this cache on every video frame.
      const movingMedia = Array.from(this.backgroundLayers.keys()).some(key => {
        const image = this.images.backgrounds[key];
        return image && !/^(IMG|IMAGE)$/i.test(image.tagName || '');
      });
      if (movingMedia) return this._paintBackground(context, layout);
      const key = JSON.stringify([this.width, this.height, this.dpr, layout,
        Array.from(this.backgroundLayers, ([id, layer]) => [id, layer.weight, this.images.backgrounds[id]?.src || ''])]);
      if (!this.backgroundCache) this.backgroundCache = this.canvas.ownerDocument.createElement('canvas');
      if (key !== this.backgroundCacheKey) {
        this.backgroundCache.width = this.canvas.width;
        this.backgroundCache.height = this.canvas.height;
        const paint = this.backgroundCache.getContext('2d');
        paint.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        this._paintBackground(paint, layout);
        this.backgroundCacheKey = key;
      }
      copySurface(context, this.backgroundCache);
    }

    _paintBackground(context, layout) {
      context.fillStyle = (this.assets.backgrounds || {})[this.background]?.color || '#f1eee6';
      // Fractional CSS dimensions can round up to an extra backing pixel. Fill
      // that complete pixel too so repeated frames never retain an old edge.
      context.fillRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);
      // Source-over needs cumulative normalized alphas to yield a true weighted crossfade.
      let cumulative = 0;
      this.backgroundLayers.forEach((layer, key) => {
        if (layer.weight <= 0) return;
        cumulative += layer.weight;
        const config = (this.assets.backgrounds || {})[key] || {};
        const background = this.shellContext;
        background.globalAlpha = 1;
        background.globalCompositeOperation = 'source-over';
        background.fillStyle = config.color || '#f1eee6';
        background.fillRect(0, 0, this.shellCanvas.width / this.dpr, this.shellCanvas.height / this.dpr);
        const img = this.images.backgrounds[key];
        if (img) {
          background.drawImage(img, ...this._backgroundPlacement(img, config, layout));
        }
        context.globalAlpha = layer.weight / cumulative;
        copySurface(context, this.shellCanvas);
      });
      context.globalAlpha = 1;
    }

    _backgroundPlacement(img, config, layout) {
      if (config.cameraCoupled) {
        // Register photographed inches and the source hanging point to the print's coordinates.
        const registered = geometry(this.width, this.height, this.currentSize, 'Untethered', layout, undefined, this.shape);
        const physicalWidth = registered.ppi * Math.max(20, number(config.cameraFieldWidthIn, 68)) * layout.scale;
        const sourceX = clamp(number(config.sourceAnchorX, 0.5), 0.001, 0.999);
        const sourceY = clamp(number(config.sourceAnchorY, 0.375), 0.001, 0.999);
        const centerX = registered.centerX;
        const centerY = registered.centerY;
        const aspect = img.width / img.height;
        // Keep the photo and product on the SAME scale. Fitting a finite room on a short
        // screen intentionally leaves page-colored margins, rather than cropping its
        // furniture or silently changing the apparent size of the artwork.
        const width = physicalWidth;
        const height = width / aspect;
        return [centerX - width * sourceX, centerY - height * sourceY, width, height];
      }
      const cover = Math.max(this.width / img.width, this.height / img.height) * layout.scale;
      const width = img.width * cover;
      const height = img.height * cover;
      return [this.width / 2 - width * layout.anchorX, this.height / 2 - height * layout.anchorY, width, height];
    }

    _drawEnvironmentLighting(context, bounds, layout) {
      context.save();
      ringPath(context, bounds.outer, bounds.art);
      context.clip('evenodd');
      context.globalCompositeOperation = 'multiply';
      this.backgroundLayers.forEach((layer, key) => {
        const img = this.images.backgrounds[key];
        if (!img || layer.weight <= 0) return;
        const config = (this.assets.backgrounds || {})[key] || {};
        const lighting = lightingFor(config);
        if (lighting.environmentAmount <= 0) return;
        context.globalAlpha = clamp(lighting.environmentAmount * layer.weight, 0, 0.3);
        // Low-pass the room photograph before applying only its broad light distribution.
        context.filter = 'blur(' + Math.max(7, lighting.environmentBlur) * this.dpr + 'px)';
        context.drawImage(img, ...this._backgroundPlacement(img, config, layout));
      });
      context.restore();
    }

    _drawShadow(context, layout, artGeometry) {
      const shadow = this.shadowContext;
      clearSurface(shadow, this.shadowCanvas);
      let extraX = 0, extraY = 0;
      let framed = 0;
      const bounds = shellGeometry(this.width, this.height, this.currentSize, layout, this.matMotion.value, this.shape);
      this.layers.forEach(layer => {
        if (layer.selection.presentation === 'Untethered') return;
        extraX += Math.max(0, bounds.outer.width / bounds.ppi - this.currentSize * this.shape.w) * layer.weight;
        extraY += Math.max(0, bounds.outer.height / bounds.ppi - this.currentSize * this.shape.h) * layer.weight;
        framed += layer.weight * clamp((this.matMotion.value + 0.75) / 0.75, 0, 1);
      });
      const size = (this.currentSize * this.shape.w + extraX) * artGeometry.ppi;
      const sizeY = (this.currentSize * this.shape.h + extraY) * artGeometry.ppi;
      const x = artGeometry.centerX - size / 2;
      const y = artGeometry.centerY - sizeY / 2;
      const lighting = this._displayedLighting();
      const ppi = artGeometry.ppi;
      // Park the opaque caster outside the surface and translate only its shadow
      // back. Erasing a caster at the product edge also erases the shadow in the
      // antialiased boundary pixels, exposing a pale line around the artwork.
      // An integer backing-pixel translation preserves the original subpixel phase;
      // the visible product geometry itself remains continuous and unsnapped.
      const casterOffset = Math.ceil((this.width + size + Math.abs(x) + 2) * this.dpr) / this.dpr;
      const passes = [
        { blur: lighting.castBlur, x: lighting.castX, y: lighting.castY, alpha: lighting.castOpacity },
        { blur: lighting.contactBlur, x: lighting.contactX, y: lighting.contactY, alpha: lighting.contactOpacity }
      ];
      passes.forEach(pass => {
        shadow.save();
        shadow.fillStyle = '#342a23';
        shadow.shadowColor = 'rgba(38,29,22,' + clamp(pass.alpha * lighting.strength * mix(lighting.unframedStrength, 1, framed), 0, 1) + ')';
        shadow.shadowBlur = (pass.blur * ppi * lighting.softness + 1) * this.dpr;
        shadow.shadowOffsetX = (casterOffset + pass.x * ppi * mix(lighting.unframedLift, 1, framed)) * this.dpr;
        shadow.shadowOffsetY = pass.y * ppi * mix(lighting.unframedLift, 1, framed) * this.dpr;
        shadow.fillRect(x - casterOffset, y, size, sizeY);
        shadow.restore();
      });
      copySurface(context, this.shadowCanvas);
    }

    _drawMat(context, bounds, mat) {
      const img = this.images.mats[mat];
      const config = (this.assets.mats || {})[mat] || {};
      context.save();
      ringPath(context, bounds.opening, bounds.art);
      context.clip('evenodd');
      context.fillStyle = mat === 'Black' ? '#242423' : mat === 'White' ? '#eeede7' : '#e7e1d0';
      context.fillRect(bounds.opening.x, bounds.opening.y, bounds.opening.width, bounds.opening.height);
      if (img) {
        const pattern = context.createPattern(img, 'repeat');
        const widthInches = number(config.patternWidthInches, 2);
        const heightInches = number(config.patternHeightInches, widthInches * img.height / img.width);
        if (pattern && pattern.setTransform && typeof root.DOMMatrix === 'function') {
          pattern.setTransform(new root.DOMMatrix().translate(bounds.opening.x, bounds.opening.y).scale(widthInches * bounds.ppi / img.width, heightInches * bounds.ppi / img.height));
          context.fillStyle = pattern;
          context.fillRect(bounds.opening.x, bounds.opening.y, bounds.opening.width, bounds.opening.height);
        } else if (pattern) {
          context.fillStyle = pattern;
          context.fillRect(bounds.opening.x, bounds.opening.y, bounds.opening.width, bounds.opening.height);
        }
      }
      const gradient = context.createLinearGradient(bounds.opening.x, bounds.opening.y, bounds.opening.x + bounds.opening.width, bounds.opening.y + bounds.opening.height);
      gradient.addColorStop(0, 'rgba(255,255,255,0.025)');
      gradient.addColorStop(0.5, 'rgba(255,255,255,0)');
      gradient.addColorStop(1, 'rgba(35,29,22,0.035)');
      context.fillStyle = gradient;
      context.fillRect(bounds.opening.x, bounds.opening.y, bounds.opening.width, bounds.opening.height);
      this._drawMatDepth(context, bounds, this._displayedLighting());
      context.restore();
    }

    _drawMatDepth(context, bounds, lighting) {
      // _drawMat's even-odd clip excludes the print aperture throughout every depth pass.
      const opening = bounds.opening;
      const ppi = bounds.ppi;
      const strength = lighting.strength * lighting.recessStrength;
      const spread = ppi * (lighting.frameDepth * 0.96 + 0.10 * lighting.softness);
      const topDepth = Math.min(bounds.matPx * 0.8, Math.max(0.5, spread));
      const leftDepth = topDepth * 0.78;
      const contactDepth = Math.max(0.4, ppi * 0.055);
      const shade = (x1, y1, x2, y2, rectangle, opacity) => {
        const gradient = context.createLinearGradient(x1, y1, x2, y2);
        const alpha = clamp(opacity * strength, 0, 0.8);
        gradient.addColorStop(0, 'rgba(24,21,18,' + alpha + ')');
        gradient.addColorStop(0.22, 'rgba(24,21,18,' + alpha * 0.62 + ')');
        gradient.addColorStop(0.63, 'rgba(24,21,18,' + alpha * 0.17 + ')');
        gradient.addColorStop(1, 'rgba(24,21,18,0)');
        context.fillStyle = gradient;
        context.fillRect(...rectangle);
      };
      // Upper-left illumination: the top and left inner rails cast into the recessed mat.
      shade(opening.x, opening.y, opening.x, opening.y + topDepth,
        [opening.x, opening.y, opening.width, topDepth], 0.30);
      shade(opening.x, opening.y, opening.x + leftDepth, opening.y,
        [opening.x, opening.y, leftDepth, opening.height], 0.23);
      // Small lower/right contact falloff completes the cavity without an outlined rectangle.
      shade(opening.x, opening.y + opening.height, opening.x, opening.y + opening.height - contactDepth,
        [opening.x, opening.y + opening.height - contactDepth, opening.width, contactDepth], 0.075);
      shade(opening.x + opening.width, opening.y, opening.x + opening.width - contactDepth, opening.y,
        [opening.x + opening.width - contactDepth, opening.y, contactDepth, opening.height], 0.055);

      // A raised print casts onto the mat. Its opaque caster is inside the clipped-out aperture,
      // therefore only the down-right shadow reaches the mat, never the artwork pixels.
      const lift = lighting.printLift;
      const shadowStrength = lighting.strength * lighting.printShadowStrength;
      const passes = [
        { blur: lift * 0.95 + 0.055, x: lift * 0.42, y: lift * 0.65, alpha: 0.19 },
        { blur: 0.032, x: 0.012, y: 0.019, alpha: 0.16 }
      ];
      passes.forEach(pass => {
        context.save();
        context.fillStyle = '#211c18';
        context.shadowColor = 'rgba(25,21,18,' + clamp(pass.alpha * shadowStrength, 0, 0.8) + ')';
        context.shadowBlur = (pass.blur * ppi * lighting.softness + 0.35) * this.dpr;
        context.shadowOffsetX = pass.x * ppi * this.dpr;
        context.shadowOffsetY = pass.y * ppi * this.dpr;
        context.fillRect(bounds.art.x, bounds.art.y, bounds.art.width, bounds.art.height);
        context.restore();
      });
    }

    _drawFrame(context, bounds, finish) {
      const img = this.images.frames[finish];
      const config = (this.assets.frames || {})[finish] || {};
      context.save();
      ringPath(context, bounds.outer, bounds.opening);
      context.clip('evenodd');
      if (img) {
        const border = number(config.border, number(config.sliceBorder, 192));
        nineSlice(img.width, img.height, border, bounds.outer, bounds.facePx, 1 / this.dpr).forEach(slice => { context.drawImage(img, ...slice); });
      } else {
        context.fillStyle = ({ 'Seraph White': '#e5e4de', 'Nocturne Black': '#262626', 'Atelier Wood': '#99754b', 'Midas Gold': '#ad884b', 'Midnight Silver': '#828587' })[finish] || '#e5e4de';
        context.fillRect(bounds.outer.x, bounds.outer.y, bounds.outer.width, bounds.outer.height);
      }
      const gradient = context.createLinearGradient(bounds.outer.x, bounds.outer.y, bounds.outer.x + bounds.outer.width, bounds.outer.y + bounds.outer.height);
      gradient.addColorStop(0, 'rgba(255,255,255,0.035)');
      gradient.addColorStop(0.48, 'rgba(255,255,255,0)');
      gradient.addColorStop(1, 'rgba(24,20,16,0.025)');
      context.fillStyle = gradient;
      context.fillRect(bounds.outer.x, bounds.outer.y, bounds.outer.width, bounds.outer.height);
      context.restore();
    }
  }

  const helpers = { SIZES, PRESENTATIONS, dimensions, shapeOf, styleKey, spring, layoutFor, geometry, shellGeometry, nineSlice, lightingFor, surfaceReflectionFor, reflectionAt, reflectionPlacement };
  ViuletGalleryRenderer.geometry = helpers;
  root.ViuletGalleryRenderer = ViuletGalleryRenderer;
  if (typeof module === 'object' && module.exports) module.exports = { ViuletGalleryRenderer, ...helpers };
})(typeof window !== 'undefined' ? window : globalThis);
